import type { GeoSetTargetable, Targeting } from '@feathr/blackbox';

/**
 * Sort geo set targetables: the account default set first, then alphabetically
 * (case-insensitive) by name. Pure — does not mutate the input array.
 */
export function sortGeoSets(
  models: GeoSetTargetable[],
  defaultGeoSetId?: string,
): GeoSetTargetable[] {
  return [...models].sort((a, b) => {
    const aIsDefault = a.isDefault(defaultGeoSetId);
    const bIsDefault = b.isDefault(defaultGeoSetId);
    if (aIsDefault && !bIsDefault) {
      return -1;
    }
    if (!aIsDefault && bIsDefault) {
      return 1;
    }
    return (a.get('name') || '').localeCompare(b.get('name') || '', undefined, {
      sensitivity: 'base',
    });
  });
}

/**
 * Apply the active name-column sort on top of the default ordering. With no active sort
 * the input (default set first, then alphabetical) is returned unchanged. Pure.
 */
export function applyNameSort(
  models: GeoSetTargetable[],
  sort: ReadonlyArray<{ desc?: boolean }>,
): GeoSetTargetable[] {
  if (sort.length === 0) {
    return models;
  }
  const [{ desc = false }] = sort;
  const sorted = [...models].sort((a, b) =>
    (a.get('name') || '').localeCompare(b.get('name') || '', undefined, { sensitivity: 'base' }),
  );
  return desc ? sorted.reverse() : sorted;
}

/**
 * Tally geo_set targetings per `target_data` id, ignoring entries without `target_data`.
 * Returns a Map of geo set id -> number of campaigns targeting that set.
 */
export function getCampaignCountsByGeoSetId(targetings: Targeting[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const targeting of targetings) {
    const targetData = targeting.get('target_data');
    if (targetData) {
      counts.set(targetData, (counts.get(targetData) ?? 0) + 1);
    }
  }
  return counts;
}
